/**
 * Single source of truth for the 17 task types Humaniti supports at launch.
 *
 * Used by:
 * - ForBuildersPage / ForHumansPage — "Cover every modality" section, rendered
 *   as a tabbed grid of cards (one tab per modality).
 * - PricingPage — the per-task-type rate grid (count + names).
 *
 * Adding a task type: append to the right modality's `taskTypes` array and
 * update PricingPage's PLACEHOLDER_PRICING_ROWS in the same commit so the
 * pricing grid stays exhaustive.
 *
 * Counts (sanity check, total = 17):
 *   Vision           4   (Image bbox, Polygon, Segmentation, Keypoint)
 *   Language         7   (Text NER, Classification, Sentiment, RLHF preference,
 *                         Writing, Coreference, Relations and moderation)
 *   Audio            2   (Transcription, Classification)
 *   Video            2   (Frame-level, Segmentation)
 *   3D & Document    2   (3D point cloud, Document parsing)
 */

export interface TaskType {
  /** Display name. Sentence case, no trailing period. */
  name: string;
  /** 1-2 sentence description. Brand voice: declarative, terse, periods end things. */
  description: string;
  /** Output formats / file shapes Humaniti emits for this task type. Mono-rendered. */
  outputs: string[];
}

export interface Modality {
  /** Tab label. */
  label: string;
  /** Stable id used as the Tabs primitive's `value`. */
  id: string;
  /** 1-line modality intro shown above the task-type grid in each panel. */
  intro: string;
  taskTypes: TaskType[];
}

export const MODALITIES: Modality[] = [
  {
    id: "vision",
    label: "Vision",
    intro: "Pixel-precise labeling for image and frame data.",
    taskTypes: [
      {
        name: "Image bbox",
        description: "Tight rectangles around objects. Multi-class, multi-instance.",
        outputs: ["COCO", "YOLO", "Pascal VOC"],
      },
      {
        name: "Polygon",
        description:
          "Multi-vertex outlines for irregular shapes. Higher precision than bboxes when objects do not align to axes.",
        outputs: ["COCO", "GeoJSON"],
      },
      {
        name: "Segmentation",
        description: "Pixel-level masks per object or class. Semantic, instance, or panoptic.",
        outputs: ["PNG masks", "COCO RLE"],
      },
      {
        name: "Keypoint",
        description:
          "Named points on a body, face, or pose skeleton. 17-point COCO body, 68-point face, custom skeletons.",
        outputs: ["COCO"],
      },
    ],
  },
  {
    id: "language",
    label: "Language",
    intro: "Span, document, and dialogue annotation across the NLP stack.",
    taskTypes: [
      {
        name: "Text NER",
        description:
          "Named-entity recognition. Tag spans as Person, Org, Location, Date, or custom types.",
        outputs: ["CoNLL", "JSONL spans"],
      },
      {
        name: "Text classification",
        description:
          "Single-label or multi-label categorisation. Topic, intent, language, content type.",
        outputs: ["JSONL"],
      },
      {
        name: "Sentiment",
        description:
          "Polarity (positive, negative, neutral) or fine-grained emotion. Span-level if needed.",
        outputs: ["JSONL"],
      },
      {
        name: "RLHF preference",
        description: "Pairwise comparison of model outputs with optional reasoning trace.",
        outputs: ["JSONL chosen/rejected"],
      },
      {
        name: "Writing",
        description:
          "Prompted generation with quality criteria. Used for SFT, instruction-tuning, and evals.",
        outputs: ["JSONL prompt+response"],
      },
      {
        name: "Coreference",
        description:
          "Link mentions of the same entity across a passage. Anaphora, ellipsis, cross-document.",
        outputs: ["CoNLL with cluster IDs"],
      },
      {
        name: "Relations and moderation",
        description: "Entity-pair relationships plus content policy labels. Multi-label per item.",
        outputs: ["JSONL"],
      },
    ],
  },
  {
    id: "audio",
    label: "Audio",
    intro: "Speech, music, and environmental sound from raw audio streams.",
    taskTypes: [
      {
        name: "Transcription",
        description:
          "Verbatim text from speech with optional speaker diarisation and word-level timestamps.",
        outputs: ["JSONL", "SRT", "WebVTT"],
      },
      {
        name: "Classification",
        description: "Tag clips by speaker, language, environment, music genre, or emotion.",
        outputs: ["JSONL"],
      },
    ],
  },
  {
    id: "video",
    label: "Video",
    intro: "Per-frame and temporal annotation across video sequences.",
    taskTypes: [
      {
        name: "Frame-level",
        description:
          "Per-frame bbox or classification with optional object tracking IDs across frames.",
        outputs: ["COCO Video", "MOT"],
      },
      {
        name: "Segmentation",
        description:
          "Per-frame pixel masks. Used for temporal action segmentation and motion analysis.",
        outputs: ["COCO Video RLE"],
      },
    ],
  },
  {
    id: "3d-document",
    label: "3D & Document",
    intro: "Point clouds for autonomy and robotics; structured extraction for forms and PDFs.",
    taskTypes: [
      {
        name: "3D point cloud",
        description:
          "Bounding boxes or segmentation in LiDAR or depth point clouds. Used for autonomous-driving perception and robotics.",
        outputs: ["KITTI", "custom JSON"],
      },
      {
        name: "Document parsing",
        description: "Extract structured fields from PDFs, scans, and forms. OCR-aware.",
        outputs: ["JSONL with field locations"],
      },
    ],
  },
];

/** Total task-type count, used in the section intro paragraph + spec checks. */
export const TASK_TYPE_COUNT = MODALITIES.reduce((n, m) => n + m.taskTypes.length, 0);
